import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import ThemeSentimentMatrix from './ThemeSentimentMatrix';
import { 
  MessageSquareText, 
  ThumbsUp, 
  ThumbsDown, 
  Minus, 
  Hash,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';


// Sentiment colors - Vibrant and distinct
// Positive: Emerald green (success, approval)
// Negative: Rose red (concern, disapproval)  
// Neutral: Slate gray (balanced, undecided)
// Mixed: Amber orange (complex, varied)
const SENTIMENT_COLORS = {
  positive: '#10B981',  // Emerald-500 - vibrant green
  negative: '#EF4444',  // Red-500 - clear red
  neutral: '#64748B',   // Slate-500 - neutral gray
  mixed: '#F59E0B'      // Amber-500 - attention orange
};

// Stance colors - Clear distinction
const STANCE_COLORS = {
  support: '#10B981',   // Emerald-500 - approval green
  oppose: '#EF4444',    // Red-500 - opposition red
  unclear: '#64748B'    // Slate-500 - undecided gray
};

// Smart ordering for demographic values - same logic as Analysis.jsx
const getOrderedDemographicValues = (values, columnName) => {
  if (!values || values.length === 0) return [];
  
  const lowerName = columnName?.toLowerCase() || '';
  
  const extractNumber = (str) => {
    const s = str.toLowerCase().trim();
    if (s.startsWith('<')) return parseInt(s.match(/\d+/)?.[0] || '0') - 0.5;
    if (s.startsWith('>') || s.endsWith('+')) return parseInt(s.match(/\d+/)?.[0] || '999') + 0.5;
    const match = s.match(/(\d+)/);
    return match ? parseInt(match[1]) : 999;
  };

  // Age, Tenure, Experience, Years
  if (lowerName.includes('age') || lowerName.includes('età') || lowerName.includes('tenure') || 
      lowerName.includes('experience') || lowerName.includes('years') || lowerName.includes('anni') ||
      lowerName.includes('service') || lowerName.includes('anzianità') || lowerName.includes('seniority')) {
    return [...values].sort((a, b) => extractNumber(a) - extractNumber(b));
  }
  
  // Education
  if (lowerName.includes('education') || lowerName.includes('degree') || lowerName.includes('istruzione')) {
    const educationOrder = ['elementary', 'middle', 'high school', 'diploma', 'bachelor', 'laurea', 'master', 'mba', 'phd', 'doctorate', 'dottorato'];
    return [...values].sort((a, b) => {
      const lowerA = a.toLowerCase();
      const lowerB = b.toLowerCase();
      const idxA = educationOrder.findIndex(e => lowerA.includes(e));
      const idxB = educationOrder.findIndex(e => lowerB.includes(e));
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.localeCompare(b);
    });
  }
  
  // Job Level
  if (lowerName.includes('level') || lowerName.includes('livello') || lowerName.includes('grade') || lowerName.includes('position')) {
    const levelOrder = ['intern', 'entry', 'junior', 'associate', 'mid', 'senior', 'lead', 'manager', 'director', 'vp', 'executive', 'ceo'];
    return [...values].sort((a, b) => {
      const lowerA = a.toLowerCase();
      const lowerB = b.toLowerCase();
      const idxA = levelOrder.findIndex(e => lowerA.includes(e));
      const idxB = levelOrder.findIndex(e => lowerB.includes(e));
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.localeCompare(b);
    });
  }
  
  // Detect numeric patterns
  const hasNumericPattern = values.some(v => /^[<>]?\d+[-–]?\d*\+?$/.test(v.trim()));
  if (hasNumericPattern) {
    return [...values].sort((a, b) => extractNumber(a) - extractNumber(b));
  }
  
  return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
};

export default function OpenEndedAnalysis({ simulation, stimulus, population }) {
  const [aggregation, setAggregation] = useState(null);
  const [themeMatrix, setThemeMatrix] = useState(null);
  const [comments, setComments] = useState([]);
  const [demographicBreakdown, setDemographicBreakdown] = useState(null);
  const [breakdownGroupBy, setBreakdownGroupBy] = useState('');
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total_count: 0 });
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [sentimentFilter, setSentimentFilter] = useState('');
  const [stanceFilter, setStanceFilter] = useState('');
  const [themeFilter, setThemeFilter] = useState('');
  const [demographicField, setDemographicField] = useState('');
  const [demographicValue, setDemographicValue] = useState('');
  
  // AI Insights
  const [insights, setInsights] = useState(null);
  const [generatingInsights, setGeneratingInsights] = useState(false);

  useEffect(() => {
    if (simulation?.id) {
      fetchData();
    }
  }, [simulation?.id]);

  useEffect(() => {
    if (simulation?.id) {
      fetchComments();
    }
  }, [simulation?.id, sentimentFilter, stanceFilter, themeFilter, demographicField, demographicValue, pagination.page]);

  useEffect(() => {
    if (simulation?.id && breakdownGroupBy) {
      fetchDemographicBreakdown();
    }
  }, [simulation?.id, breakdownGroupBy]);

  useEffect(() => {
    // Set default group by when population loads - skip ID columns
    if (population?.columns?.length > 0 && !breakdownGroupBy) {
      const meaningfulCol = population.columns.find(col => {
        const name = col.name.toLowerCase();
        return !name.includes('_id') && name !== 'id' && !name.endsWith('id');
      });
      setBreakdownGroupBy(meaningfulCol?.name || population.columns[0].name);
    }
  }, [population]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [aggRes, matrixRes] = await Promise.all([
        api.get(`/simulations/${simulation.id}/aggregation`),
        api.get(`/simulations/${simulation.id}/theme-sentiment-matrix`)
      ]);
      setAggregation(aggRes.data);
      setThemeMatrix(matrixRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load analysis data');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('page_size', 10);
      if (sentimentFilter) params.append('sentiment', sentimentFilter);
      if (stanceFilter) params.append('stance', stanceFilter);
      if (themeFilter) params.append('theme', themeFilter);
      if (demographicField && demographicValue) {
        params.append('demographic_field', demographicField);
        params.append('demographic_value', demographicValue);
      }
      
      const res = await api.get(`/simulations/${simulation.id}/open-ended-comments?${params.toString()}`);
      setComments(res.data.comments);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchDemographicBreakdown = async () => {
    if (!breakdownGroupBy) return;
    try {
      const res = await api.get(`/simulations/${simulation.id}/demographic-breakdown?group_by=${breakdownGroupBy}`);
      setDemographicBreakdown(res.data);
    } catch (error) {
      console.error('Error fetching demographic breakdown:', error);
    }
  };

  const [insightQuestion, setInsightQuestion] = useState('');

  const generateInsights = async (question = '') => {
    setGeneratingInsights(true);
    try {
      const res = await api.post(
        `/simulations/${simulation.id}/open-ended-insights`, 
        { question: question || null }
      );
      // Backend returns { insights, summary_stats, generated_at }
      setInsights(res.data);
      toast.success('Insights generated successfully');
    } catch (error) {
      console.error('Error generating insights:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate insights');
    } finally {
      setGeneratingInsights(false);
    }
  };

  // Generate plain-English summary for open-ended analysis
  const generateOpenEndedSummary = (agg, themes) => {
    if (!agg) return '';
    
    const positiveRate = agg.sentiment_percentages?.positive || 0;
    const negativeRate = agg.sentiment_percentages?.negative || 0;
    const topThemeNames = themes.slice(0, 2).map(t => t.name.toLowerCase());
    
    let sentimentDescription = 'mixed';
    if (positiveRate > 60) sentimentDescription = 'mostly positive';
    else if (positiveRate > 45) sentimentDescription = 'generally positive';
    else if (negativeRate > 60) sentimentDescription = 'mostly negative';
    else if (negativeRate > 45) sentimentDescription = 'generally negative';
    
    const themeText = topThemeNames.length > 0 
      ? `, with key themes around ${topThemeNames.join(' and ')}`
      : '';
    
    return `Reactions are ${sentimentDescription}${themeText}.`;
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive': return <ThumbsUp className="w-4 h-4 text-emerald-500" />;
      case 'negative': return <ThumbsDown className="w-4 h-4 text-red-500" />;
      case 'mixed': return <AlertCircle className="w-4 h-4 text-amber-500" />;
      default: return <Minus className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Prepare chart data
  const sentimentData = aggregation?.sentiment_distribution 
    ? Object.entries(aggregation.sentiment_distribution).map(([name, value]) => ({ name, value }))
    : [];
  
  const stanceData = aggregation?.stance_distribution
    ? Object.entries(aggregation.stance_distribution).map(([name, value]) => ({ name, value }))
    : [];

  const topThemes = aggregation?.top_themes 
    ? Object.entries(aggregation.top_themes)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([name, count]) => ({ name, count }))
    : [];

  return (
    <div className="space-y-6">
      {/* Plain-English Summary */}
      {aggregation && (
        <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed">
            {generateOpenEndedSummary(aggregation, topThemes)}
          </p>
        </div>
      )}
      
      {/* Synthetic Data Warning */}
      <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-800">Synthetic Comments Generated by Simulation</h4>
            <p className="text-sm text-amber-700 mt-1">
              These responses are AI-generated based on the population demographics. 
              They represent simulated opinions, not real survey responses.
            </p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border border-border">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-blue-50">
                <MessageSquareText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{aggregation?.total_responses || 0}</p>
                <p className="text-sm text-slate-500">Total Responses</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border border-border">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-emerald-50">
                <ThumbsUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{aggregation?.sentiment_percentages?.positive?.toFixed(1) || 0}%</p>
                <p className="text-sm text-slate-500">Positive Sentiment</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border border-border">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-red-50">
                <ThumbsDown className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{aggregation?.sentiment_percentages?.negative?.toFixed(1) || 0}%</p>
                <p className="text-sm text-slate-500">Negative Sentiment</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border border-border">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-blue-50">
                <Hash className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{aggregation?.theme_count || 0}</p>
                <p className="text-sm text-slate-500">Unique Themes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sentiment Distribution */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Sentiment Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <RechartsPie>
                <Pie
                  data={sentimentData.filter(d => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  dataKey="value"
                  paddingAngle={2}
                  label={({ name, percent }) => percent >= 0.03 ? `${name} ${(percent * 100).toFixed(0)}%` : null}
                  labelLine={{ stroke: '#94A3B8', strokeWidth: 1 }}
                >
                  {sentimentData.filter(d => d.value > 0).map((entry) => (
                    <Cell key={entry.name} fill={SENTIMENT_COLORS[entry.name] || '#6B7280'} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [`${value} responses`, 'Count']}
                  contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                  itemStyle={{ color: '#fff' }}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                />
              </RechartsPie>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Themes */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Top Themes</CardTitle>
          </CardHeader>
          <CardContent>
            {topThemes.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={topThemes} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                    itemStyle={{ color: '#fff' }}
                  />
                  <Bar dataKey="count" fill="#6366F1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                No themes found
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stance Distribution */}
      {stanceData.length > 0 && (
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Stance Distribution</CardTitle>
            <p className="text-sm text-slate-500">Support vs Opposition analysis</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stanceData.filter(d => d.value > 0)} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="category" dataKey="name" />
                <YAxis type="number" />
                <Tooltip 
                  formatter={(value) => [`${value} responses (${((value / aggregation?.total_responses) * 100).toFixed(1)}%)`, 'Count']}
                  contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                  itemStyle={{ color: '#fff' }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {stanceData.filter(d => d.value > 0).map((entry) => (
                    <Cell key={entry.name} fill={STANCE_COLORS[entry.name] || '#6B7280'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Theme × Sentiment Matrix */}
      {themeMatrix?.matrix?.length > 0 && (
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Theme × Sentiment Matrix</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-medium text-slate-700">Theme</th>
                    <th className="text-center py-2 px-3 font-medium text-blue-600">Positive</th>
                    <th className="text-center py-2 px-3 font-medium text-red-500">Negative</th>
                    <th className="text-center py-2 px-3 font-medium text-slate-500">Neutral</th>
                    <th className="text-center py-2 px-3 font-medium text-amber-500">Mixed</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-700">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {themeMatrix.matrix.slice(0, 10).map((row, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 font-medium">{row.theme}</td>
                      <td className="text-center py-2 px-3">
                        <span className={cn(
                          "inline-block px-2 py-0.5 rounded text-xs",
                          row.positive_pct > 50 ? "bg-blue-100 text-blue-700" : "text-slate-500"
                        )}>
                          {row.positive_pct}%
                        </span>
                      </td>
                      <td className="text-center py-2 px-3">
                        <span className={cn(
                          "inline-block px-2 py-0.5 rounded text-xs",
                          row.negative_pct > 50 ? "bg-red-100 text-red-700" : "text-slate-500"
                        )}>
                          {row.negative_pct}%
                        </span>
                      </td>
                      <td className="text-center py-2 px-3 text-slate-500 text-xs">{row.neutral_pct}%</td>
                      <td className="text-center py-2 px-3 text-slate-500 text-xs">{row.mixed_pct}%</td>
                      <td className="text-right py-2 px-3 font-medium">{row.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Demographic Breakdown */}
      {population?.columns?.length > 0 && (
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Demographic Breakdown</CardTitle>
              <Select value={breakdownGroupBy} onValueChange={setBreakdownGroupBy}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Group by" />
                </SelectTrigger>
                <SelectContent>
                  {population.columns.map(col => (
                    <SelectItem key={col.name} value={col.name}>{col.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {demographicBreakdown?.breakdown ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart 
                  data={(() => {
                    const entries = Object.entries(demographicBreakdown.breakdown).map(([group, data]) => ({
                      group,
                      positive: data.sentiment_percentages?.positive || 0,
                      negative: data.sentiment_percentages?.negative || 0,
                      neutral: data.sentiment_percentages?.neutral || 0,
                      mixed: data.sentiment_percentages?.mixed || 0,
                      total: data.total
                    }));
                    const orderedGroups = getOrderedDemographicValues(entries.map(e => e.group), breakdownGroupBy);
                    return orderedGroups.map(group => entries.find(e => e.group === group)).filter(Boolean);
                  })()}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" domain={[0, 100]} unit="%" />
                  <YAxis type="category" dataKey="group" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    formatter={(value) => `${value.toFixed(1)}%`}
                    contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '4px' }}
                    labelStyle={{ color: '#fff' }}
                    itemStyle={{ color: '#fff' }}
                  />
                  <Legend />
                  <Bar dataKey="positive" stackId="a" fill={SENTIMENT_COLORS.positive} name="Positive" />
                  <Bar dataKey="neutral" stackId="a" fill={SENTIMENT_COLORS.neutral} name="Neutral" />
                  <Bar dataKey="mixed" stackId="a" fill={SENTIMENT_COLORS.mixed} name="Mixed" />
                  <Bar dataKey="negative" stackId="a" fill={SENTIMENT_COLORS.negative} name="Negative" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Comment Browser */}
      <Card className="border border-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Comment Browser</CardTitle>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              {pagination.total_count} comments
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4 p-3 rounded-lg bg-slate-50">
            <Select value={sentimentFilter || "all"} onValueChange={(v) => setSentimentFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Sentiment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sentiments</SelectItem>
                <SelectItem value="positive">Positive</SelectItem>
                <SelectItem value="negative">Negative</SelectItem>
                <SelectItem value="neutral">Neutral</SelectItem>
                <SelectItem value="mixed">Mixed</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={stanceFilter || "all"} onValueChange={(v) => setStanceFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Stance" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stances</SelectItem>
                <SelectItem value="support">Support</SelectItem>
                <SelectItem value="oppose">Oppose</SelectItem>
                <SelectItem value="unclear">Unclear</SelectItem>
              </SelectContent>
            </Select>
            
            {topThemes.length > 0 && (
              <Select value={themeFilter || "all"} onValueChange={(v) => setThemeFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Theme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Themes</SelectItem>
                  {topThemes.map(t => (
                    <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {population?.columns?.length > 0 && (
              <>
                <Select value={demographicField || "none"} onValueChange={(val) => { setDemographicField(val === "none" ? "" : val); setDemographicValue(''); }}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Demo. Field" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Filter</SelectItem>
                    {population.columns.map(col => (
                      <SelectItem key={col.name} value={col.name}>{col.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {demographicField && (
                  <Select value={demographicValue || "all"} onValueChange={(v) => setDemographicValue(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Value" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {population.columns.find(c => c.name === demographicField)?.sample_values?.map(val => (
                        <SelectItem key={val} value={val}>{val}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </>
            )}
            
            {(sentimentFilter || stanceFilter || themeFilter || demographicField) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setSentimentFilter('');
                  setStanceFilter('');
                  setThemeFilter('');
                  setDemographicField('');
                  setDemographicValue('');
                }}
              >
                Clear All
              </Button>
            )}
          </div>

          {/* Comments List */}
          <div className="space-y-3">
            {comments.length > 0 ? comments.map((comment, idx) => (
              <div 
                key={comment.id || idx} 
                className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {getSentimentIcon(comment.sentiment)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700">{comment.answer_text}</p>
                    
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <Badge variant="outline" className={cn(
                        "text-xs",
                        comment.sentiment === 'positive' && "border-emerald-300 text-emerald-700",
                        comment.sentiment === 'negative' && "border-red-300 text-red-700",
                        comment.sentiment === 'neutral' && "border-gray-300 text-gray-700",
                        comment.sentiment === 'mixed' && "border-amber-300 text-amber-700"
                      )}>
                        {comment.sentiment}
                      </Badge>
                      
                      <Badge variant="outline" className="text-xs">
                        {comment.stance}
                      </Badge>
                      
                      {comment.themes?.slice(0, 3).map((theme, i) => (
                        <Badge 
                          key={i} 
                          variant="secondary" 
                          className="text-xs cursor-pointer hover:bg-blue-100"
                          onClick={() => setThemeFilter(theme)}
                        >
                          #{theme}
                        </Badge>
                      ))}
                    </div>
                    
                    {comment.attributes && (
                      <div className="flex flex-wrap gap-1 mt-2 text-xs text-slate-500">
                        {Object.entries(comment.attributes).slice(0, 4).map(([key, val]) => (
                          <span key={key} className="bg-slate-100 px-1.5 py-0.5 rounded">
                            {key}: {val}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-slate-400">
                No comments match the current filters
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                disabled={pagination.page <= 1}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              
              <span className="text-sm text-slate-500">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                disabled={pagination.page >= pagination.total_pages}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Insights */}
      <Card className="border border-border bg-gradient-to-br from-blue-50/50 to-white">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-500" />
              AI Insights
            </CardTitle>
            <div className="flex items-center gap-2">
              {insights && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setInsights(null)}
                  className="text-xs"
                >
                  Clear
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => generateInsights()}
                disabled={generatingInsights}
                className={insights ? "bg-blue-100 text-blue-700 hover:bg-blue-200" : ""}
              >
                {generatingInsights ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {insights ? 'Regenerate' : 'Generate Insights'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {insights ? (
            <div className="space-y-4">
              {/* Generated timestamp */}
              <p className="text-xs text-slate-400">
                Generated: {new Date(insights.generated_at).toLocaleString()}
              </p>
              
              {/* Insights text */}
              <div className="prose prose-sm max-w-none prose-headings:text-slate-800 prose-p:text-slate-600 prose-li:text-slate-600 prose-strong:text-slate-700">
                <div className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">
                  {insights.insights}
                </div>
              </div>
              
              {/* Summary stats badges */}
              {insights.summary_stats?.top_themes?.length > 0 && (
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-xs font-medium text-slate-500 mb-2">Top Themes Analyzed</p>
                  <div className="flex flex-wrap gap-2">
                    {insights.summary_stats.top_themes.map(([theme, count], idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {theme}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p className="text-sm font-medium">AI-Powered Analysis</p>
              <p className="text-xs mt-1 max-w-sm mx-auto">
                Get executive summaries, theme analysis, sentiment insights, and actionable recommendations powered by GPT-4
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Theme-Sentiment Matrix - Phase 3 */}
      <ThemeSentimentMatrix simulationId={simulation.id} />
    </div>
  );
}
